import type { ColumnDef } from "@tanstack/react-table";

import clsx from "clsx";

import type { PerfRow, ViewTab } from "../types";

import { SkeletonLoader } from "../../SkeletonLoader";

/** Body rows to fill the skeleton with, matching the canvas's 8. */
const SKELETON_ROW_COUNT = 8;

/**
 * Height of a real loaded row (px) — kept identical to the `skeletonRowHeight`
 * the page passes to `TableDesktop` so resolving to data causes no reflow.
 */
const ROW_HEIGHT = 74;

/**
 * The source cell's leading element mirrors the loaded `SourceCell` for the
 * active tab: a round flag for Country, nothing at all for the name-only
 * Rebate program and Campaign tabs, and the 48×40 icon/thumb tile otherwise.
 */
const SourceLead = ({ tab }: { tab: ViewTab }) => {
  if (tab === "country")
    return (
      <SkeletonLoader
        variant="rounded"
        motionReduce
        width={24}
        height={24}
        radius="full"
      />
    );
  if (tab === "rebate" || tab === "campaign") return null;
  return (
    <SkeletonLoader
      variant="rounded"
      motionReduce
      width={48}
      height={40}
      radius="lg"
    />
  );
};

type PerformanceTableSkeletonProps = {
  /** The real column defs, so skeleton column widths match the loaded table. */
  columns: ColumnDef<PerfRow, unknown>[];
  tab: ViewTab;
  /** Mirrors `TableDesktop`'s `stickyFirstColumn` so the source column pins the same way. */
  stickyFirstColumn?: boolean;
};

/**
 * Desktop table loading skeleton with a **skeleton header row** and per-tab
 * source cells — the two things the shared `TableDesktopSkeletonLoader` can't
 * express (it renders the real headers, and one uniform bar per cell).
 *
 * Rendered through `TableDesktop`'s `skeletonComponent` slot, so it sits inside
 * the same rounded scroll frame as the real table and inherits its border,
 * radius and scroll affordances rather than redrawing them.
 */
export const PerformanceTableSkeleton = ({
  columns,
  tab,
  stickyFirstColumn = false,
}: PerformanceTableSkeletonProps) => {
  // Column 0 is always the source column; the rest are right-aligned metrics.
  const [sourceColumn, ...metricColumns] = columns;
  // Only the Source tab's loaded cell has a second meta line beneath the name.
  const hasMetaLine =
    tab !== "country" && tab !== "rebate" && tab !== "campaign";

  return (
    <div data-testid="performance-table-skeleton">
      <table className="w-full table-fixed border-separate border-spacing-0">
        <thead>
          <tr className="bg-surface-foreground">
            <th
              className={clsx(
                "px-3 py-2 text-start",
                stickyFirstColumn &&
                  "sticky start-0 z-[3] bg-surface-foreground",
              )}
              style={{ width: sourceColumn?.size }}
            >
              {/* Matches the real header's h-8 sort-button box so the skeleton
                  header isn't shorter and doesn't jump on load. */}
              <div className="flex h-8 items-center">
                <SkeletonLoader
                  variant="rounded"
                  motionReduce
                  radius="md"
                  width={110}
                  height={14}
                />
              </div>
            </th>
            {metricColumns.map((column, index) => (
              <th
                key={column.id ?? index}
                className="px-3 py-2 text-start"
                style={{ width: column.size }}
              >
                <div className="flex h-8 items-center justify-end">
                  <SkeletonLoader
                    variant="rounded"
                    motionReduce
                    radius="md"
                    width={58}
                    height={14}
                  />
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: SKELETON_ROW_COUNT }, (_, rowIndex) => (
            <tr key={rowIndex} style={{ height: ROW_HEIGHT }}>
              <td
                className={clsx(
                  "border-b border-border-primary-base p-3",
                  stickyFirstColumn &&
                    "sticky start-0 z-[1] bg-surface-primary",
                )}
                style={{ width: sourceColumn?.size }}
              >
                <div className="flex items-center gap-3">
                  <SourceLead tab={tab} />
                  <div className="flex min-w-0 flex-1 flex-col gap-1">
                    <SkeletonLoader
                      variant="rounded"
                      motionReduce
                      radius="md"
                      width="72%"
                      height={13}
                    />
                    {hasMetaLine && (
                      <SkeletonLoader
                        variant="rounded"
                        motionReduce
                        radius="md"
                        width="46%"
                        height={11}
                      />
                    )}
                  </div>
                </div>
              </td>
              {metricColumns.map((column, index) => (
                <td
                  key={column.id ?? index}
                  className="border-b border-border-primary-base p-3"
                  style={{ width: column.size }}
                >
                  <div className="flex justify-end">
                    <SkeletonLoader
                      variant="rounded"
                      motionReduce
                      radius="md"
                      width={54}
                      height={13}
                    />
                  </div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Desktop page-chrome skeletons
// Shown only on first entry (isInitialLoading) — once the report has data,
// a query change re-skeletons the stat cards and table only.
// ---------------------------------------------------------------------------

/** Title + last-updated line. */
export const PageHeaderSkeleton = () => (
  <div className="flex flex-col gap-3">
    <SkeletonLoader
      variant="rounded"
      motionReduce
      width={300}
      height={48}
      radius="lg"
    />
    <SkeletonLoader
      variant="rounded"
      motionReduce
      radius="md"
      width={230}
      height={16}
    />
  </div>
);

/** Date-range field + filter button row. */
export const ControlsSkeleton = () => (
  <div className="flex items-center gap-4">
    <SkeletonLoader
      variant="rounded"
      motionReduce
      width={260}
      height={40}
      radius="lg"
    />
    <SkeletonLoader
      variant="rounded"
      motionReduce
      width={120}
      height={40}
      radius="lg"
    />
  </div>
);

/** "View by" label + segmented tabs, with the Columns / Export actions end-aligned. */
export const InsightsBarSkeleton = () => (
  <div className="flex flex-wrap items-center gap-4">
    <SkeletonLoader
      variant="rounded"
      motionReduce
      radius="md"
      width={64}
      height={16}
    />
    <SkeletonLoader
      variant="rounded"
      motionReduce
      width={300}
      height={40}
      radius="xl"
    />
    <div className="ms-auto flex items-center gap-4">
      <SkeletonLoader
        variant="rounded"
        motionReduce
        width={130}
        height={40}
        radius="lg"
      />
      <SkeletonLoader
        variant="rounded"
        motionReduce
        width={130}
        height={40}
        radius="lg"
      />
    </div>
  </div>
);
